import { useEffect, useSyncExternalStore } from 'react';

/**
 * Human labels for route segments that are ids.
 *
 * A breadcrumb reading `650000000000000000000101` tells the user nothing and looks
 * broken. Only the screen that loaded the record knows the name, so it registers one
 * here and the breadcrumb picks it up.
 *
 * A module-level store rather than context: the breadcrumb lives in the shell, above
 * every route, so a provider would have to wrap the whole app to carry a value that
 * flows upward. Registrations are removed on unmount, so a stale name cannot survive
 * onto another record.
 */

const labels = new Map<string, string>();
const listeners = new Set<() => void>();
let version = 0;

function emit(): void {
  version += 1;
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Registers `label` for `segment` while the calling component is mounted. */
export function useCrumbLabel(segment: string | undefined, label: string | undefined): void {
  useEffect(() => {
    if (!segment || !label) return;

    labels.set(segment, label);
    emit();

    return () => {
      labels.delete(segment);
      emit();
    };
  }, [segment, label]);
}

/** Re-renders the breadcrumb when a label is registered or removed. */
export function useCrumbLabels(): (segment: string) => string | undefined {
  useSyncExternalStore(
    subscribe,
    () => version,
    () => version,
  );
  return (segment: string) => labels.get(segment);
}
